<template>
  <div class="pure-u-1 sso-buttons">
    <p class="sso-hint">
      Use Tavour in the app? Sign in the same way here, so your club joins your existing account.
    </p>

    <div class="text-red sso-error" role="alert" v-if="error">
      {{ error.text }}
      <a v-if="error.link" class="link" :href="error.link.href" target="_blank" rel="noopener">{{ error.link.label }}</a>
    </div>

    <p v-if="googleBlocked" class="sso-notice">
      To sign in with Google, open this page in Safari or Chrome.
    </p>
    <button v-else class="pure-button sso-button" type="button" :disabled="busy !== null"
            :aria-busy="busy === providers.google" @click="signIn(providers.google)">
      <svg class="sso-logo" viewBox="0 0 18 18" aria-hidden="true">
        <path fill="#EA4335" d="M9 3.48c1.69 0 2.83.73 3.48 1.34l2.54-2.48C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.91 2.26C4.6 5.05 6.62 3.48 9 3.48z"/>
        <path fill="#4285F4" d="M17.64 9.2c0-.74-.06-1.28-.19-1.84H9v3.34h4.96c-.1.83-.64 2.08-1.84 2.92l2.84 2.2c1.7-1.57 2.68-3.88 2.68-6.62z"/>
        <path fill="#FBBC05" d="M3.88 10.78A5.54 5.54 0 0 1 3.58 9c0-.62.11-1.22.29-1.78L.96 4.96A9.008 9.008 0 0 0 0 9c0 1.45.35 2.82.96 4.04l2.92-2.26z"/>
        <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.84-2.2c-.76.53-1.78.9-3.12.9-2.38 0-4.4-1.57-5.12-3.74L.97 13.04C2.45 15.98 5.48 18 9 18z"/>
      </svg>
      {{ busy === providers.google ? 'Opening Google...' : 'Continue with Google' }}
    </button>

    <button class="pure-button sso-button" type="button" :disabled="busy !== null"
            :aria-busy="busy === providers.apple" @click="signIn(providers.apple)">
      <svg class="sso-logo" viewBox="0 0 814 1000" aria-hidden="true">
        <path fill="#000" d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"/>
      </svg>
      {{ busy === providers.apple ? 'Opening Apple...' : 'Continue with Apple' }}
    </button>

    <p class="sso-divider"><span>or use your email</span></p>
  </div>
</template>

<script>
  /* eslint-env browser */
  import { PROVIDERS, FAILED_MESSAGE, start, takeError, isGoogleBlocked } from '../../lib/sso';

  // "Continue with Google / Apple". It also shows the error of a sign-in that
  // just came back, so the message sits next to the buttons.
  export default {
    name: 'SsoButtons',
    data() {
      return {
        busy: null,
        error: takeError(),
        providers: PROVIDERS,
        googleBlocked: isGoogleBlocked(),
      };
    },
    mounted() {
      if (this.error) this.$el.scrollIntoView();
      window.addEventListener('pageshow', this.onPageShow);
    },
    beforeDestroy() {
      window.removeEventListener('pageshow', this.onPageShow);
    },
    methods: {
      signIn(provider) {
        this.busy = provider;
        this.error = null;
        start(provider).catch(() => {
          this.busy = null;
          this.error = { text: FAILED_MESSAGE };
        });
      },
      // The browser can restore this page from its cache after Back at Google / Apple.
      onPageShow(event) {
        if (event.persisted) this.busy = null;
      },
    },
  };
</script>

<style scoped>
  .sso-hint {
    margin-top: 0;
  }
  .sso-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 44px;
    margin-bottom: 12px;
    border: 1px solid #747775;
    border-radius: 4px;
    background: #fff;
    color: #1f1f1f;
    font-weight: 500;
  }
  .sso-button:disabled {
    opacity: 0.6;
  }
  .sso-button:focus-visible {
    outline: 2px solid #284cab;
    outline-offset: 2px;
  }
  .sso-logo {
    width: 20px;
    height: 20px;
    margin-right: 12px;
  }
  .sso-notice {
    color: #333;
  }
  .sso-error {
    margin-bottom: 12px;
  }
  .sso-divider {
    display: flex;
    align-items: center;
    margin: 16px 0;
    color: #555;
  }
  .sso-divider::before,
  .sso-divider::after {
    content: '';
    flex: 1;
    border-top: 1px solid #ccc;
  }
  .sso-divider span {
    padding: 0 10px;
  }
</style>



// WEBPACK FOOTER //
// SsoButtons.vue?d08369f8